import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { productsAPI, ecuTuningAPI } from '../services/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useProductTranslation } from '../hooks/useProductTranslation';
import { toast } from 'react-toastify';
import SEO from '../components/SEO';
import BrandLogoMatcher from '../components/BrandLogoMatcher';
import Stage1Modal from '../components/Stage1Modal';
import Stage2Modal from '../components/Stage2Modal';
import { formatPrice, STAGE_PRICES, convertPrice, getCurrencyByLanguage, getCurrencySymbol } from '../utils/currency';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [activeStage, setActiveStage] = useState(1);
  const [stage1ModalOpen, setStage1ModalOpen] = useState(false);
  const [showEcuForm, setShowEcuForm] = useState(false);
  const [stage2ModalOpen, setStage2ModalOpen] = useState(false);
  const [ecuForm, setEcuForm] = useState({
    fullName: '',
    phone: '',
    email: '',
    address: '',
    manufacturer: '',
    model: '',
    yearOfProduction: '',
    vinNumber: '',
    powerLimiter: '',
    airIntake: '',
    exhaustSystem: '',
    otherModifications: '',
    unlocking: false,
    powerLimiterBlock: false,
    stage1: false,
    immobilizerRemoval: false,
    stage2: false,
    dtcRemoval: false,
    paymentTransfer: false,
    paymentCash: false,
  });
  const [ecuFormSubmitting, setEcuFormSubmitting] = useState(false);
  const [ecuFormErrors, setEcuFormErrors] = useState({});
  const [fromStage, setFromStage] = useState(null);
  const [stage1GraphExists, setStage1GraphExists] = useState(true);
  const [stage2GraphExists, setStage2GraphExists] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();

  // Call translation hook at top level (must not be in conditional render path)
  const translatedProduct = useProductTranslation(product);

  const parseTechnicalFeatures = (features) => {
    if (!features) return [];
    try {
      const parsed = typeof features === 'string' ? JSON.parse(features) : features;
      if (!parsed || typeof parsed !== 'object') return [];
      return Object.entries(parsed)
        .filter(([key]) => key && key.toLowerCase() !== 'description')
        .map(([key, value]) => ({ key, value }));
    } catch (error) {
      return [];
    }
  };
  
  // Check if user came from Tuning page or is viewing a tuning product URL
  const isFromTuning = location.state?.fromTuning || location.pathname.startsWith('/tuning/');
  const isTuningProduct = location.pathname.startsWith('/tuning/');

  const getProductModelName = () => {
    if (!product?.name) return '';

    const yearMatch = product.name.match(/(\d{4})-(\d{4})/);
    const withoutManufacturer = product.name
      .replace(product.manufacturer || '', '')
      .trim();
    const baseName = yearMatch
      ? withoutManufacturer.split(yearMatch[0])[0]
      : withoutManufacturer;

    return baseName.replace(/[()]/g, '').trim();
  };

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  // Auto-check stage checkbox when ECU form opens based on which stage button was clicked
  useEffect(() => {
    if (showEcuForm && fromStage) {
      if (fromStage === 1) {
        setEcuForm(prev => ({ ...prev, stage1: true }));
      } else if (fromStage === 2) {
        setEcuForm(prev => ({ ...prev, stage2: true }));
      }
    }
  }, [showEcuForm, fromStage]);

  useEffect(() => {
    if (!showEcuForm || !product) return;

    setEcuForm(prev => ({
      ...prev,
      manufacturer: prev.manufacturer || product.manufacturer || '',
      model: prev.model || getProductModelName(),
    }));
  }, [showEcuForm, product]);

  // Auto-select Stage 2 if Stage 1 is not available
  useEffect(() => {
    if (product && product.stage1_available === false && activeStage === 1) {
      setActiveStage(2);
    }
  }, [product]);

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const PHONE_REGEX = /^\+?\d{7,15}$/;

  // Helper function to build graph URL
  const buildGraphUrl = (stage) => {
    if (!product?.manufacturer || !product?.name) return null;
    
    // Extract year range from product name (e.g., "2020-2024" or "2021-2023")
    const yearMatch = product.name.match(/(\d{4})-(\d{4})/);
    if (!yearMatch) {
      console.log('No year match found in product name:', product.name);
      return null;
    }
    
    const yearFrom = yearMatch[1];
    const yearTo = yearMatch[2];
    
    // Remove manufacturer and extract model name (everything before year range)
    // Remove parentheses and extra spaces
    const modelPart = product.name
      .replace(product.manufacturer, '')
      .split(yearMatch[0])[0] // Split at year range and take first part
      .replace(/[()]/g, '') // Remove parentheses
      .trim();
    
    // Build S3 URL: {bucket_url}/graph/{manufacturer}_{model}_{year_from}_{year_to}_{stage}.jpeg
    const manufacturer = product.manufacturer.toLowerCase();
    const modelLower = modelPart.toLowerCase().replace(/\s+/g, '_'); // Replace spaces with underscores
    const stageStr = stage === 1 ? 'Stage1' : 'Stage2';
    
    const bucketUrl = process.env.REACT_APP_BUCKET_URL;
    const graphUrl = `${bucketUrl}/graph/${manufacturer}_${modelLower}_${yearFrom}_${yearTo}_${stageStr}.jpeg`;
    
    console.log('Generated graph URL:', graphUrl);
    console.log('Product name:', product.name);
    console.log('Manufacturer:', manufacturer);
    console.log('Model:', modelLower);
    console.log('Year range:', yearFrom, '-', yearTo);
    
    return graphUrl;
  };

  const fetchProduct = async () => {
    try {
      setLoading(true);
      
      // Use tuning API if viewing from /tuning/:slug route
      const response = isTuningProduct
        ? await ecuTuningAPI.getBySlug(slug)
        : await productsAPI.getBySlug(slug);
        
      const product = response.data;
      
      setProduct(product);
    } catch (error) {
      toast.error(t('toast.productNotFound'), { autoClose: 1000 });
      navigate(isTuningProduct ? '/tuning' : '/products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated()) {
      toast.error(t('toast.loginRequired'), { autoClose: 1000 });
      navigate('/login');
      return;
    }
    if (isAdding) return; // Prevent multiple simultaneous requests
    
    try {
      setIsAdding(true);
      await addToCart(product.id, quantity);
      navigate('/cart');
    } catch (error) {
      // Error handled in context
    } finally {
      setIsAdding(false);
    }
  };

  const handleAddStageToCart = async (stage) => {
    if (!isAuthenticated()) {
      toast.error(t('toast.loginRequired'), { autoClose: 1000 });
      navigate('/login');
      return;
    }
    if (isAdding) return; // Prevent multiple simultaneous requests
    
    try {
      setIsAdding(true);
      // Add the product to cart with quantity 1
      // The stage information is cosmetic - the actual product is being added
      await addToCart(product.id, 1);
      toast.success(`${t('products.addStage' + stage + 'ToCart')} - ${formatPrice(stage === 1 ? STAGE_PRICES.stage1 : STAGE_PRICES.stage2, language)}`, { autoClose: 1000 });
      navigate('/cart');
    } catch (error) {
      // Error handled in context
    } finally {
      setIsAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!product) return null;

  // Check if this is a tuning product (either from /tuning/:slug route OR has category handheld_tuners)
  const isTuningLayout = isTuningProduct || product.category === 'handheld_tuners';

  return (
    <>
      <SEO
        title={product.name}
        description={product.short_description || product.description}
        keywords={`${product.name}, ${product.category || t('seo.productDetail.defaultCategory')}, ${t('seo.productDetail.keywordsBase')}, ${product.manufacturer || ''}`}
        image={product.image_url}
        type="product"
      />

      {/* Hero Section with Breadcrumb */}
      <section className={`relative py-16 ${
        isTuningLayout
          ? 'bg-gradient-to-br from-racing-black via-racing-darkGray to-gray-900 text-white'
          : 'bg-gradient-to-br from-gray-100 via-white to-gray-200 text-gray-900'
      }`}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-2">{product.name}</h1>
            {product.manufacturer && (
              <p className={`text-xl ${isTuningLayout ? 'text-gray-300' : 'text-gray-700'}`}>
                {product.manufacturer} {(product.model_year || product.year_range) && `• ${product.model_year || product.year_range}`}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      <div className={`min-h-screen py-12 ${
        isTuningLayout
          ? 'bg-gradient-to-b from-gray-900 to-black text-white'
          : 'bg-gradient-to-b from-gray-50 via-white to-gray-100 text-gray-900'
      }`}>
        <div className="container-custom">
          {isTuningLayout ? (
            /* Tuning Mode: Full-width layout with logo and stages */
            <div className="max-w-5xl mx-auto">
              {/* Logo Section */}
              <motion.div
                className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl p-8 mb-8 border border-gray-700"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="w-48 h-48 bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl flex items-center justify-center p-6 shadow-lg border border-gray-600">
                    {product.manufacturer && (
                      <BrandLogoMatcher 
                        manufacturer={product.manufacturer} 
                        alt={`${product.manufacturer} logo`}
                        className="max-w-full max-h-full object-contain filter brightness-110"
                      />
                    )}
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <h2 className="text-3xl font-bold mb-2 text-white">{product.name}</h2>
                    <p className="text-gray-300 text-lg">{t('products.ecuTuningSolution')}</p>
                  </div>
                </div>
              </motion.div>

              {/* Stage Selection Tabs */}
              <motion.div
                className="flex gap-4 mb-8 justify-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {/* Only show Stage 1 button if stage1_available is true or undefined (backwards compatibility) */}
                {(product.stage1_available === undefined || product.stage1_available === true) && (
                  <button
                    onClick={() => setActiveStage(1)}
                    className={`px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 ${
                      activeStage === 1
                        ? 'bg-gradient-to-r from-orange-600 to-red-700 text-white shadow-xl'
                        : 'bg-gray-800 text-gray-300 border border-gray-700 hover:border-orange-600'
                    }`}
                  >
                    Stage 1
                  </button>
                )}
                <button
                  onClick={() => setActiveStage(2)}
                  className={`px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 ${
                    activeStage === 2
                      ? 'bg-gradient-to-r from-racing-red to-red-700 text-white shadow-xl'
                      : 'bg-gray-800 text-gray-300 border border-gray-700 hover:border-racing-red'
                  }`}
                >
                  Stage 2
                </button>
              </motion.div>

              {/* Contact CTA */}
                {product && product.stage1_available === false && (
                  <div className="bg-red-900/30 rounded-lg p-4 mb-6 border-l-4 border-red-500">
                    <h3 className="font-bold text-lg mb-3 text-red-400 flex items-center">{t('products.ctaTitle')}</h3>
                    <p className="text-gray-300 flex items-start">{t('products.ctaDescription')}</p>
                  </div>
              )}  

              {/* Stage Content with Animation */}
              <motion.div
                key={`stage-${activeStage}`}
                initial={{ opacity: 0, x: activeStage === 1 ? -50 : 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: activeStage === 1 ? 50 : -50 }}
                transition={{ duration: 0.4 }}
                className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-700"
              >
                <div className={`bg-gradient-to-r ${activeStage === 1 ? 'from-orange-600 to-red-700' : 'from-racing-red to-red-700'} p-6`}>
                  <h2 className="text-2xl md:text-3xl font-bold text-white flex items-center">
                    <span className={`${activeStage === 1 ? 'bg-white text-racing-red' : 'bg-white text-orange-600'} rounded-full w-10 h-10 flex items-center justify-center mr-3 font-display shadow-lg`}>
                      {activeStage}
                    </span>
                    {activeStage === 1 ? t('products.stage1Title') : t('products.stage2Title')}
                  </h2>
                </div>
                
                <div className="p-6 md:p-8">
                  {activeStage === 1 ? (
                    <>
                      <p className="text-gray-300 mb-6 text-lg leading-relaxed">{t('products.stage1Intro')}</p>
                      
                      <div className="bg-gray-900 rounded-xl p-6 shadow-lg mb-6 border border-gray-700">
                        <h3 className="font-bold text-lg mb-3 text-racing-red flex items-center">
                          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                          </svg>
                          {t('products.stage1ModificationTitle')}
                        </h3>
                        <ul className="space-y-2 text-gray-300">
                          <li className="flex items-start">
                            <span className="text-racing-red mr-2 mt-1">•</span>
                            <span>{t('products.stage1Mod1')}</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-racing-red mr-2 mt-1">•</span>
                            <span>{t('products.stage1Mod2')}</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-racing-red mr-2 mt-1">•</span>
                            <span>{t('products.stage1Mod3')}</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-racing-red mr-2 mt-1">•</span>
                            <span>{t('products.stage1Mod4')}</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-purple-900/30 rounded-lg p-4 mb-6 border-l-4 border-purple-500">
                        <h3 className="font-bold mb-2 text-purple-300">{t('products.stage1Additional')}</h3>
                        <p className="text-gray-300 flex items-start">
                          <span className="text-purple-400 mr-2 mt-1">🔥</span>
                          <span>{t('products.stage1PopBangs')}</span>
                        </p>
                      </div>
                                            
                      <p className="text-gray-300 mb-6 leading-relaxed bg-blue-900/30 p-4 rounded-lg border-l-4 border-blue-500">
                        {t('products.stage1Goal')}
                      </p>

                      <div className="bg-gradient-to-br from-green-900/40 to-emerald-900/40 rounded-xl p-6 border border-green-700 mb-8">
                        <h3 className="font-bold text-lg mb-3 text-green-400 flex items-center">
                          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd"/>
                          </svg>
                          {t('products.stage1GainsTitle')}
                        </h3>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="bg-gray-900 rounded-lg p-4 shadow-lg border border-green-800">
                            <p className="text-green-300 font-semibold">{t('products.stage1Power')}</p>
                          </div>
                          <div className="bg-gray-900 rounded-lg p-4 shadow-lg border border-green-800">
                            <p className="text-green-300 font-semibold">{t('products.stage1Torque')}</p>
                          </div>
                        </div>
                        <p className="text-gray-400 text-sm italic mt-4">{t('products.stage1Disclaimer')}</p>
                                                
                        {/* Performance Graph */}
                        {stage1GraphExists && buildGraphUrl(1) && (
                          <div className="mb-4">
                            <img
                              src={buildGraphUrl(1)}
                              alt={`${product.manufacturer} ${product.name} Stage 1 Performance Graph`}
                              className="w-full rounded-lg shadow-lg border border-green-800"
                              onError={(e) => {
                                console.error('Stage 1 graph failed to load:', e.target.src);
                                setStage1GraphExists(false);
                              }}
                              onLoad={() => console.log('Stage 1 graph loaded successfully')}
                              loading="lazy"
                            />
                          </div>
                        )}
                        
                      </div>

                      {/* Stage 1 Button */}
                      <div className="flex justify-center">
                        <motion.button
                          onClick={() => {
                            setStage1ModalOpen(true);
                          }}
                          whileHover={{ scale: 1.05 }}
                          className="w-full sm:w-auto bg-gradient-to-r from-orange-600 to-red-700 hover:from-red-700 hover:to-orange-600 text-white font-bold py-4 px-8 rounded-xl transition-all transform shadow-lg text-lg"
                        >
                          {t('products.stage1Info') || 'Learn About Stage 1'} - {formatPrice(STAGE_PRICES.stage1, language)}
                        </motion.button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-gray-300 mb-4 text-lg">{t('products.stage2Intro')}</p>
                      <div className="bg-orange-900/30 rounded-lg p-4 mb-6 border-l-4 border-orange-500">
                        <ul className="space-y-2 text-gray-300">
                          <li className="flex items-start">
                            <span className="text-orange-400 mr-2 mt-1">✓</span>
                            <span className="font-medium">{t('products.stage2Req1')}</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-orange-400 mr-2 mt-1">✓</span>
                            <span className="font-medium">{t('products.stage2Req2')}</span>
                          </li>
                        </ul>
                      </div>
                      
                      <div className="bg-gray-900 rounded-xl p-6 shadow-lg mb-6 border border-gray-700">
                        <h3 className="font-bold text-lg mb-3 text-orange-400 flex items-center">
                          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/>
                          </svg>
                          {t('products.stage2WorkTitle')}
                        </h3>
                        <ul className="space-y-2 text-gray-300">
                          <li className="flex items-start">
                            <span className="text-orange-400 mr-2 mt-1">•</span>
                            <span>{t('products.stage2Work1')}</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-orange-400 mr-2 mt-1">•</span>
                            <span>{t('products.stage2Work2')}</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-orange-400 mr-2 mt-1">•</span>
                            <span>{t('products.stage2Work3')}</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-orange-400 mr-2 mt-1">•</span>
                            <span>{t('products.stage2Work4')}</span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-purple-900/30 rounded-lg p-4 mb-6 border-l-4 border-purple-500">
                        <h3 className="font-bold mb-2 text-purple-300">{t('products.stage2Additional')}</h3>
                        <p className="text-gray-300 flex items-start">
                          <span className="text-purple-400 mr-2 mt-1">🔥</span>
                          <span>{t('products.stage2PopBangs')}</span>
                        </p>
                      </div>
                      
                      <p className="text-gray-300 mb-6 leading-relaxed bg-blue-900/30 p-4 rounded-lg border-l-4 border-blue-500">
                        {t('products.stage2Help')}
                      </p>
                      
                      <div className="bg-gradient-to-br from-green-900/40 to-emerald-900/40 rounded-xl p-6 border border-green-700 mb-8">
                        <h3 className="font-bold text-lg mb-3 text-green-400 flex items-center">
                          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd"/>
                          </svg>
                          {t('products.stage2GainsTitle')}
                        </h3>
                        
                        {/* Performance Graph */}
                        {stage2GraphExists && buildGraphUrl(2) && (
                          <div className="mb-4">
                            <img
                              src={buildGraphUrl(2)}
                              alt={`${product.manufacturer} ${product.name} Stage 2 Performance Graph`}
                              className="w-full rounded-lg shadow-lg border border-green-800"
                              onError={(e) => {
                                console.error('Stage 2 graph failed to load:', e.target.src);
                                setStage2GraphExists(false);
                              }}
                              onLoad={() => console.log('Stage 2 graph loaded successfully')}
                              loading="lazy"
                            />
                          </div>
                        )}
                        
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="bg-gray-900 rounded-lg p-4 shadow-lg border border-green-800">
                            <p className="text-green-300 font-semibold">{t('products.stage2Power')}</p>
                          </div>
                          <div className="bg-gray-900 rounded-lg p-4 shadow-lg border border-green-800">
                            <p className="text-green-300 font-semibold">{t('products.stage2Torque')}</p>
                          </div>
                        </div>
                        <p className="text-gray-400 text-sm italic mt-4">{t('products.stage2Disclaimer')}</p>
                      </div>

                      {/* Stage 2 Button */}
                      <div className="flex justify-center">
                        <motion.button
                          onClick={() => {
                            setFromStage(2);
                            setEcuFormErrors({});
                            setStage2ModalOpen(true);
                          }}
                          whileHover={{ scale: 1.05 }}
                          className="w-full sm:w-auto bg-red-900/40 text-white font-bold py-4 px-8 rounded-xl border-2 border-racing-red text-center text-lg hover:bg-red-900/60 transition-colors cursor-pointer"
                        >
                          {t('products.stage2LearnMore') || 'Learn more about Stage 2'} - {t('products.stage2Price') || 'From'} {formatPrice(STAGE_PRICES.stage2, language)}<br />
                        </motion.button>
                      </div>
                    </>
                  )}
                </div>
              </motion.div>

              {/* ECU Tuning Form Modal */}
              {showEcuForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                  <motion.div
                    className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8 flex flex-col max-h-[90vh]"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="bg-gradient-to-r from-racing-red to-red-700 p-6 flex-shrink-0">
                      <h2 className="text-2xl font-bold text-white">{t('home.ecuForm.title')}</h2>
                      <p className="text-white/90 mt-1">{product.manufacturer} {getProductModelName() || product.name}</p>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                      <div className="p-6 md:p-8">
                        <form className="space-y-6">
                        {/* Contact Information Section */}
                        <div>
                          <h3 className="text-xl font-bold text-racing-red mb-4">
                            {t('home.ecuForm.contactData')}
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">
                                {t('home.ecuForm.fullName')} <span className="text-red-600">*</span>
                              </label>
                              <input
                                type="text"
                                value={ecuForm.fullName}
                                onChange={(e) => setEcuForm({ ...ecuForm, fullName: e.target.value })}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-racing-red text-sm text-gray-900 ${
                                  ecuFormErrors.fullName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                }`}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">
                                {t('home.ecuForm.phone')} <span className="text-red-600">*</span>
                              </label>
                              <input
                                type="tel"
                                value={ecuForm.phone}
                                onChange={(e) => setEcuForm({ ...ecuForm, phone: e.target.value })}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-racing-red text-sm text-gray-900 ${
                                  ecuFormErrors.phone ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                }`}
                              />
                              {ecuFormErrors.phone && (
                                <p className="text-red-600 text-xs mt-1">{ecuFormErrors.phone}</p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">
                                {t('home.ecuForm.email')} <span className="text-red-600">*</span>
                              </label>
                              <input
                                type="email"
                                value={ecuForm.email}
                                onChange={(e) => setEcuForm({ ...ecuForm, email: e.target.value })}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-racing-red text-sm text-gray-900 ${
                                  ecuFormErrors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                }`}
                              />
                              {ecuFormErrors.email && (
                                <p className="text-red-600 text-xs mt-1">{ecuFormErrors.email}</p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">
                                {t('home.ecuForm.address')}
                              </label>
                              <input
                                type="text"
                                value={ecuForm.address}
                                onChange={(e) => setEcuForm({ ...ecuForm, address: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-racing-red text-sm text-gray-900"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Motorcycle Information Section */}
                        <div className="border-t pt-6">
                          <h3 className="text-xl font-bold text-racing-red mb-4">
                            {t('home.ecuForm.motorcycleData')}
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">
                                {t('home.ecuForm.manufacturer')} <span className="text-red-600">*</span>
                              </label>
                              <input
                                type="text"
                                value={ecuForm.manufacturer || product?.manufacturer || ''}
                                onChange={(e) => setEcuForm({ ...ecuForm, manufacturer: e.target.value })}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-racing-red text-sm text-gray-900 ${
                                  ecuFormErrors.manufacturer ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                }`}
                              />
                              {ecuFormErrors.manufacturer && (
                                <p className="text-red-600 text-xs mt-1">{ecuFormErrors.manufacturer}</p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">
                                {t('home.ecuForm.model')} <span className="text-red-600">*</span>
                              </label>
                              <input
                                type="text"
                                value={ecuForm.model || getProductModelName() || ''}
                                onChange={(e) => setEcuForm({ ...ecuForm, model: e.target.value })}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-racing-red text-sm text-gray-900 ${
                                  ecuFormErrors.model ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                }`}
                              />
                              {ecuFormErrors.model && (
                                <p className="text-red-600 text-xs mt-1">{ecuFormErrors.model}</p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">
                                {t('home.ecuForm.yearOfProduction')} <span className="text-red-600">*</span>
                              </label>
                              <input
                                type="number"
                                value={ecuForm.yearOfProduction}
                                onChange={(e) => setEcuForm({ ...ecuForm, yearOfProduction: parseInt(e.target.value) })}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-racing-red text-sm text-gray-900 ${
                                  ecuFormErrors.yearOfProduction ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                }`}
                              />
                              {ecuFormErrors.yearOfProduction && <p className="text-red-600 text-xs mt-1">{ecuFormErrors.yearOfProduction}</p>}
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">
                                {t('home.ecuForm.vinNumber')}
                              </label>
                              <input
                                type="text"
                                value={ecuForm.vinNumber}
                                onChange={(e) => setEcuForm({ ...ecuForm, vinNumber: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-racing-red text-sm text-gray-900"
                              />
                            </div>
                          </div>

                          {/* Power Limiter Question */}
                          <div className="mt-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              {t('home.ecuForm.powerLimiter')}
                            </label>
                            <div className="flex gap-4">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="powerLimiter"
                                  value="yes"
                                  checked={ecuForm.powerLimiter === 'yes'}
                                  onChange={(e) => setEcuForm({ ...ecuForm, powerLimiter: e.target.value })}
                                  className="w-4 h-4"
                                />
                                <span className="text-gray-700 text-sm">{t('home.ecuForm.yes')}</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="powerLimiter"
                                  value="no"
                                  checked={ecuForm.powerLimiter === 'no'}
                                  onChange={(e) => setEcuForm({ ...ecuForm, powerLimiter: e.target.value })}
                                  className="w-4 h-4"
                                />
                                <span className="text-gray-700 text-sm">{t('home.ecuForm.no')}</span>
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Mechanical Changes Section */}
                        <div className="border-t pt-6">
                          <h3 className="text-xl font-bold text-racing-red mb-4">
                            {t('home.ecuForm.mechanicalChanges')}
                          </h3>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">
                                {t('home.ecuForm.airIntakeLabelShort')}
                              </label>
                              <textarea
                                value={ecuForm.airIntake}
                                onChange={(e) => setEcuForm({ ...ecuForm, airIntake: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-racing-red text-sm text-gray-900"
                                rows="2"
                              ></textarea>
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">
                                {t('home.ecuForm.exhaustSystem')}
                              </label>
                              <textarea
                                value={ecuForm.exhaustSystem}
                                onChange={(e) => setEcuForm({ ...ecuForm, exhaustSystem: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-racing-red text-sm text-gray-900"
                                rows="2"
                              ></textarea>
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">
                                {t('home.ecuForm.otherModifications')}
                              </label>
                              <textarea
                                value={ecuForm.otherModifications}
                                onChange={(e) => setEcuForm({ ...ecuForm, otherModifications: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-racing-red text-sm text-gray-900"
                                rows="2"
                              ></textarea>
                            </div>
                          </div>
                        </div>

                        {/* ECU Work Scope Section */}
                        <div className="border-t pt-6">
                          <h3 className="text-xl font-bold text-racing-red mb-4">
                            {t('home.ecuForm.ecuWorkScope')}
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={ecuForm.unlocking}
                                onChange={(e) => setEcuForm({ ...ecuForm, unlocking: e.target.checked })}
                                className="w-4 h-4 text-racing-red"
                              />
                              <span className="text-gray-700 text-sm">{t('home.ecuForm.unlocking')}</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={ecuForm.powerLimiterBlock}
                                onChange={(e) => setEcuForm({ ...ecuForm, powerLimiterBlock: e.target.checked })}
                                className="w-4 h-4 text-racing-red"
                              />
                              <span className="text-gray-700 text-sm">{t('home.ecuForm.powerLimiterBlock')}</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={ecuForm.stage1}
                                onChange={(e) => setEcuForm({ ...ecuForm, stage1: e.target.checked })}
                                className="w-4 h-4 text-racing-red"
                              />
                              <span className="text-gray-700 text-sm">{t('home.ecuForm.stage1')}</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={ecuForm.immobilizerRemoval}
                                onChange={(e) => setEcuForm({ ...ecuForm, immobilizerRemoval: e.target.checked })}
                                className="w-4 h-4 text-racing-red"
                              />
                              <span className="text-gray-700 text-sm">{t('home.ecuForm.immobilizerRemoval')}</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={ecuForm.stage2}
                                onChange={(e) => setEcuForm({ ...ecuForm, stage2: e.target.checked })}
                                className="w-4 h-4 text-racing-red"
                              />
                              <span className="text-gray-700 text-sm">{t('home.ecuForm.stage2')}</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={ecuForm.dtcRemoval}
                                onChange={(e) => setEcuForm({ ...ecuForm, dtcRemoval: e.target.checked })}
                                className="w-4 h-4 text-racing-red"
                              />
                              <span className="text-gray-700 text-sm">{t('home.ecuForm.dtcRemoval')}</span>
                            </label>
                          </div>
                        </div>

                        {/* Payment Method Section - Only show for Stage 1 */}
                        {fromStage !== 2 && (
                          <div className="border-t pt-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                              {t('home.ecuForm.paymentMethod')}
                            </h3>
                            <div className="space-y-3">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={ecuForm.paymentTransfer}
                                  onChange={(e) => setEcuForm({ ...ecuForm, paymentTransfer: e.target.checked })}
                                  className="w-4 h-4 text-racing-red"
                                />
                                <span className="text-gray-700 text-sm">{t('home.ecuForm.paymentTransfer')}</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={ecuForm.paymentCash}
                                  onChange={(e) => setEcuForm({ ...ecuForm, paymentCash: e.target.checked })}
                                  className="w-4 h-4 text-racing-red"
                                />
                                <span className="text-gray-700 text-sm">{t('home.ecuForm.paymentCash')}</span>
                              </label>
                            </div>
                          </div>
                        )}

                        {/* Info Note */}
                        {fromStage !== 2 && (
                        <div className="border-t pt-6 bg-blue-50 p-4 rounded-lg border-l-4 border-racing-red">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            ℹ️ {t('home.ecuForm.infoNote')}
                          </p>
                        </div>
                        )}
                        </form>
                      </div>
                    </div>

                    {/* Submit Buttons - Fixed at bottom */}
                    <div className="border-t p-6 bg-white flex gap-3 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          setShowEcuForm(false);
                          setEcuForm({
                            fullName: '',
                            phone: '',
                            email: '',
                            address: '',
                            manufacturer: '',
                            model: '',
                            yearOfProduction: '',
                            vinNumber: '',
                            powerLimiter: '',
                            airIntake: '',
                            exhaustSystem: '',
                            otherModifications: '',
                            unlocking: false,
                            powerLimiterBlock: false,
                            stage1: false,
                            immobilizerRemoval: false,
                            stage2: false,
                            dtcRemoval: false,
                            paymentTransfer: false,
                            paymentCash: false,
                          });
                          setEcuFormErrors({});
                        }}
                        className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition-colors"
                      >
                        {t('home.ecuForm.cancel')}
                      </button>
                      <button
                        type="submit"
                        disabled={ecuFormSubmitting}
                        onClick={async (e) => {
                          e.preventDefault();
                          
                          // Validate required fields and show which ones are missing
                          const errors = {};
                          const requiredMessage = t('home.ecuForm.errors.messages.Field required') || 'Field required';
                          const phoneValue = (ecuForm.phone || '').trim();
                          const emailValue = (ecuForm.email || '').trim();
                          const yearValue = ecuForm.yearOfProduction ? Number(ecuForm.yearOfProduction) : null;
                          const currentYear = new Date().getFullYear();

                          if (!ecuForm.fullName) errors.fullName = requiredMessage;
                          if (!phoneValue) errors.phone = requiredMessage;
                          if (!emailValue) errors.email = requiredMessage;
                          
                          const manufacturer = ecuForm.manufacturer || product?.manufacturer;
                          if (!manufacturer) errors.manufacturer = requiredMessage;
                          
                          const model = ecuForm.model || product?.name;
                          if (!model) errors.model = requiredMessage;
                          
                          if (!yearValue) errors.yearOfProduction = requiredMessage;

                          if (phoneValue && !PHONE_REGEX.test(phoneValue)) {
                            errors.phone = t('home.ecuForm.errors.messages.phone_invalid') || 'Invalid phone number';
                          }

                          if (emailValue && !EMAIL_REGEX.test(emailValue)) {
                            errors.email = t('home.ecuForm.errors.messages.email_invalid') || 'Invalid email address';
                          }

                          if (yearValue && (!Number.isInteger(yearValue) || yearValue < 1900 || yearValue > currentYear)) {
                            errors.yearOfProduction = t('home.ecuForm.errors.messages.year_invalid') || 'Invalid year';
                          }
                          
                          if (Object.keys(errors).length > 0) {
                            setEcuFormErrors(errors);
                            toast.error(t('home.ecuForm.missingFields') || 'Please fill in the required fields');
                            return;
                          }
                          
                          // Clear errors if validation passes
                          setEcuFormErrors({});
                          
                          setEcuFormSubmitting(true);
                          try {
                            const response = await ecuTuningAPI.submit({
                              full_name: ecuForm.fullName,
                              phone: ecuForm.phone,
                              email: ecuForm.email,
                              address: ecuForm.address || '',
                              manufacturer: ecuForm.manufacturer || product.manufacturer,
                              model: ecuForm.model || product.name,
                              year_of_production: ecuForm.yearOfProduction,
                              vin_number: ecuForm.vinNumber || '',
                              power_limiter: ecuForm.powerLimiter === 'yes',
                              air_intake: ecuForm.airIntake || '',
                              exhaust_system: ecuForm.exhaustSystem || '',
                              other_modifications: ecuForm.otherModifications || '',
                              unlocking: ecuForm.unlocking,
                              power_limiter_block: ecuForm.powerLimiterBlock,
                              immobilizer_removal: ecuForm.immobilizerRemoval,
                              stage_1: ecuForm.stage1,
                              stage_2: ecuForm.stage2,
                              dtc_removal: ecuForm.dtcRemoval,
                              payment_transfer: ecuForm.paymentTransfer,
                              payment_cash: ecuForm.paymentCash,
                            });

                            const createdRequestId = response?.data?.id;
                            const createdStatus = response?.data?.status;
                            if (!createdRequestId || createdStatus !== 'success') {
                              throw new Error('ECU request was not persisted');
                            }
                            
                            toast.success(t('home.ecuForm.submitSuccess'));
                            setShowEcuForm(false);
                            setEcuForm({
                              fullName: '',
                              phone: '',
                              email: '',
                              address: '',
                              manufacturer: '',
                              model: '',
                              yearOfProduction: '',
                              vinNumber: '',
                              powerLimiter: '',
                              airIntake: '',
                              exhaustSystem: '',
                              otherModifications: '',
                              unlocking: false,
                              powerLimiterBlock: false,
                              stage1: false,
                              immobilizerRemoval: false,
                              stage2: false,
                              dtcRemoval: false,
                              paymentTransfer: false,
                              paymentCash: false,
                            });
                          } catch (error) {
                            console.error('Error submitting ECU form:', error);

                            if (error?.message === 'Invalid API response content type') {
                              toast.error(t('home.ecuForm.submitError') || 'Failed to submit form');
                              return;
                            }
                            
                            // Handle different error response formats
                            let errorMessage = t('home.ecuForm.submitError') || 'Failed to submit form';
                            const serverErrors = {};
                            
                            if (error.response?.data?.detail) {
                              // FastAPI validation errors return an array with loc and msg
                              if (Array.isArray(error.response.data.detail)) {
                                error.response.data.detail.forEach(err => {
                                  if (err.loc && err.msg) {
                                    // err.loc is like ["body", "year_of_production"]
                                    const fieldName = err.loc[err.loc.length - 1];
                                    // Convert snake_case to camelCase for form field matching
                                    const camelCaseField = fieldName.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
                                    
                                    // Extract error code from message (e.g., "String should match pattern..." -> extract code or use full message)
                                    let errorCode = err.msg;
                                    const patterns = {
                                      'phone_invalid': 'home.ecuForm.errors.messages.phone_invalid',
                                      'email_invalid': 'home.ecuForm.errors.messages.email_invalid',
                                      'year_invalid': 'home.ecuForm.errors.messages.year_invalid',
                                      'Field required': 'home.ecuForm.errors.messages.Field required',
                                      'Input should be a valid number': 'home.ecuForm.errors.messages.Input should be a valid number',
                                      'Input should be a valid integer': 'home.ecuForm.errors.messages.Input should be a valid integer',
                                    };
                                    
                                    // Try to match the error message against known patterns
                                    let translationKey = null;
                                    for (const [pattern, key] of Object.entries(patterns)) {
                                      if (err.msg.includes(pattern)) {
                                        translationKey = key;
                                        break;
                                      }
                                    }
                                    
                                    // Get translated error message or use original
                                    const translatedErrorMsg = translationKey ? t(translationKey) : err.msg;
                                    serverErrors[camelCaseField] = translatedErrorMsg;
                                  }
                                });
                                
                                if (Object.keys(serverErrors).length > 0) {
                                  setEcuFormErrors(serverErrors);
                                  // Don't show error toast, errors are shown under fields
                                } else {
                                  // Fallback if we can't parse the error
                                  errorMessage = error.response.data.detail
                                    .map(err => err.msg || JSON.stringify(err))
                                    .join(', ');
                                  toast.error(errorMessage);
                                }
                              } else if (typeof error.response.data.detail === 'string') {
                                errorMessage = error.response.data.detail;
                                toast.error(errorMessage);
                              }
                            }
                          } finally {
                            setEcuFormSubmitting(false);
                          }
                        }}
                        className="flex-1 px-4 py-3 bg-racing-red hover:bg-red-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors"
                      >
                        {ecuFormSubmitting ? 'Submitting...' : t('home.ecuForm.submit')}
                      </button>
                    </div>
                  </motion.div>
                </div>
              )}

            </div>
          ) : (
          /* Normal Product Mode: Show stock and old cart */
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="h-48 md:h-52 bg-gray-200 rounded-lg overflow-hidden">
                {product.image_url && (
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                )}
              </div>
            </div>

            <div>
              <div className="text-4xl font-bold text-racing-red mb-6">
                {(() => {
                  const currency = getCurrencyByLanguage(language);
                  const convertedPrice = convertPrice(product.price, currency);
                  const symbol = getCurrencySymbol(currency);
                  return `${symbol}${convertedPrice}`;
                })()}
              </div>

              {product.sku && (
                <div className="mb-4 pb-4 border-b border-gray-300">
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold">{t('products.sku') || 'SKU'}:</span> <span className="font-mono text-gray-900">{product.sku}</span>
                  </p>
                </div>
              )}

              {translatedProduct.short_description && (
                <p className="text-gray-800 font-medium mb-2">
                  {translatedProduct.short_description}
                </p>
              )}

              <p className="text-gray-700 mb-6 leading-relaxed">
                {translatedProduct.description}
              </p>

              {parseTechnicalFeatures(translatedProduct.technical_features).length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3">{t('products.technicalFeatures')}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {parseTechnicalFeatures(translatedProduct.technical_features).map(({ key, value }) => (
                      <div key={key} className="text-sm text-gray-700">
                        <span className="font-semibold">{key}:</span> {String(value)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Stock Status */}
              {product.category !== 'handheld_tuners' && (
                <div className="mb-6">
                  <span className={`px-4 py-2 rounded-lg ${
                    product.stock > 200 
                      ? 'bg-blue-100 text-blue-800' 
                      : product.stock > 0 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {product.stock > 200 
                      ? t('products.madeToOrder') 
                      : product.stock > 0 
                      ? `${t('products.inStock')} (${product.stock} ${t('products.available')})` 
                      : t('products.outOfStock')}
                  </span>
                </div>
              )}

              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <label className="font-semibold">{t('shoppingCart.quantity')}</label>
                  <input
                    type="number"
                    min="1"
                    max={product.stock > 0 ? product.stock : 999}
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Math.min(product.stock > 0 ? product.stock : 999, parseInt(e.target.value) || 1)))}
                    className="w-20 px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <button
                  onClick={handleAddToCart}
                  disabled={isAdding}
                  className="btn btn-primary w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAdding ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('common.addToCart')}
                    </span>
                  ) : (
                    t('common.addToCart')
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        </div>

      </div>

      {/* Stage 1 Modal */}
      <Stage1Modal 
        isOpen={stage1ModalOpen} 
        onClose={() => setStage1ModalOpen(false)} 
        productName={product?.name}
        onOpenContactForm={() => {
          setStage1ModalOpen(false);
          setFromStage(1);
          setShowEcuForm(true);
        }}
      />

      {/* Stage 2 Modal */}
      <Stage2Modal 
        isOpen={stage2ModalOpen} 
        onClose={() => setStage2ModalOpen(false)}
        onOpenContactForm={() => {
          setStage2ModalOpen(false);
          setFromStage(2);
          setShowEcuForm(true);
        }}
      />
    </>
  );
};

export default ProductDetail;
